import { Navigate, RouteObject } from "react-router";

import * as Sentry from "@sentry/react";

import MaterialHandlingPage from "@/components/MaterialHandling/MaterialHandlingPage";
import ProcedureUpdates from "@/components/Task/ProcedureUpdates";

import PageError from "@/helpers/components/Errors/PageError";

export const MaterialHandlingRoutes: RouteObject = {
	path: "materialhandling/*",
	element: (
		<Sentry.ErrorBoundary
			fallback={({ error, componentStack, resetError }) => (
				<PageError
					error={error}
					componentStack={componentStack}
					reset={resetError}
				/>
			)}
		>
			<MaterialHandlingPage />
		</Sentry.ErrorBoundary>
	),
	children: [
		{
			index: true,
			element: <Navigate to="mypage" replace />,
		},
		{
			index: true,
			lazy: async () => {
				const { MaterialHandlingMyPage } = await import(
					"@/components/MaterialHandling/MaterialHandlingList"
				);
				return { Component: MaterialHandlingMyPage };
			},
		},
		{
			path: "mypage",
			lazy: async () => {
				const { MaterialHandlingMyPage } = await import(
					"@/components/MaterialHandling/MaterialHandlingList"
				);
				return { Component: MaterialHandlingMyPage };
			},
		},
		{
			path: "list",
			lazy: async () => {
				const { ViewMaterialHandlingList } = await import(
					"@/components/MaterialHandling/MaterialHandlingList"
				);
				return { Component: ViewMaterialHandlingList };
			},
		},
		{
			path: ":id/*",
			children: [
				{
					path: "edit",
					lazy: async () => {
						const { EditMaterialHandling } = await import(
							"@/components/MaterialHandling/EditMaterialHandling"
						);
						return { Component: EditMaterialHandling };
					},
				},
				{
					path: "editv2",
					lazy: async () => {
						const { EditMaterialHandling } = await import(
							"@/components/MaterialHandling/EditMaterialHandling"
						);
						return { Component: EditMaterialHandling };
					},
				},
				{
					path: "import",
					lazy: async () => {
						const { CreateMaterialHandling } = await import(
							"@/components/MaterialHandling/CreateMaterialHandling"
						);
						return {
							Component: () => <CreateMaterialHandling importMode={true} />,
						};
					},
				},
			],
		},
		{
			path: "view/*",
			children: [
				{
					path: ":id",
					lazy: async () => {
						const { ViewMaterialHandling } = await import(
							"@/components/MaterialHandling/ViewMaterialHandling"
						);
						return { Component: ViewMaterialHandling };
					},
				},
			],
		},
		{
			path: "tagid/:tagid",
			lazy: async () => {
				const { ViewMaterialHandling } = await import(
					"@/components/MaterialHandling/ViewMaterialHandling"
				);
				return { Component: ViewMaterialHandling };
			},
		},
		{
			path: "create/*",
			children: [
				{
					index: true,
					lazy: async () => {
						const { CreateMaterialHandling } = await import(
							"@/components/MaterialHandling/CreateMaterialHandling"
						);
						return { Component: CreateMaterialHandling };
					},
				},
				{
					path: ":id",
					lazy: async () => {
						const { CreateMaterialHandling } = await import(
							"@/components/MaterialHandling/CreateMaterialHandling"
						);
						return { Component: CreateMaterialHandling };
					},
				},
			],
		},
		{
			path: "approval/*",
			children: [
				{
					path: "pending",
					lazy: async () => {
						const { MaterialHandlingWaitingForApproval } = await import(
							"@/components/MaterialHandling/MaterialHandlingWaitingForApproval"
						);
						return { Component: MaterialHandlingWaitingForApproval };
					},
				},
				{
					path: "approve/:id/tag/:tagid",
					lazy: async () => {
						const { MaterialHandlingApproveUpdate } = await import(
							"@/components/MaterialHandling/MaterialHandlingApproveUpdate"
						);
						return { Component: MaterialHandlingApproveUpdate };
					},
				},
			],
		},
		{
			path: "settings",
			lazy: async () => {
				const { MaterialHandlingSettingsPage } = await import(
					"@/components/Application/AdminMenu/Settings/MaterialHandlingSettingsPage"
				);
				return { Component: MaterialHandlingSettingsPage };
			},
		},
		{
			path: "save/:id",
			lazy: async () => {
				const { CompleteMaterialHandling } = await import(
					"@/components/MaterialHandling/CompleteMaterialHandling"
				);
				return { Component: CompleteMaterialHandling };
			},
		},
		{
			path: "archive/*",
			children: [
				{
					index: true,
					Component: () => (
						<h1>
							Dashboard for Material handling archive is under construction
						</h1>
					),
				},
				{
					path: ":id",
					lazy: async () => {
						const { MaterialHandlingArchive } = await import(
							"@/components/MaterialHandling/MaterialHandlingArchive"
						);
						return { Component: MaterialHandlingArchive };
					},
				},
			],
		},

		{
			path: "updates/newImprov",
			element: <ProcedureUpdates page="newImprov" hasMaterialHandling={true} />,
		},
		{
			path: "updates/myImprov",
			element: <ProcedureUpdates page="myImprov" hasMaterialHandling={true} />,
		},
		{
			path: "updates/verified",
			element: <ProcedureUpdates page="verified" hasMaterialHandling={true} />,
		},
		{
			path: "procedureUpdates/declined",
			element: <ProcedureUpdates page="declined" hasMaterialHandling={true} />,
		},
	],
};
